import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useFeatureFlagLevels } from "@/hooks/use-feature-flag";
import { resolveFeatureFlagLevel, type FeatureFlagKey } from "@/lib/feature-flags";
import { CHANGELOG_ENTRIES, visibleEntries } from "@/lib/changelog/registry";
import { RELEASE_NOTES, visibleReleases } from "@/lib/changelog/release-registry";
import type { ChangelogEntry } from "@/lib/changelog/types";
import type { ReleaseNote } from "@/lib/changelog/release-types";

/**
 * Endringslogg («Nytt i Revehiet», VIB-2621).
 *
 * Synlighet følger AKTIV bruker (`useImpersonation().activeUser`), så en
 * superadmin som impersonerer ser loggen slik den brukeren ser den. «Sett»-
 * markeringen derimot skrives alltid på den FAKTISK innloggede brukeren:
 * RPC-en `mark_changelog_seen` oppdaterer kun `current_user_id()` sin rad,
 * og popupen hopper over impersonering (se ChangelogDialog).
 */

/**
 * Rolle og flagg-oppslag for aktiv bruker, delt av begge lagene i loggen og
 * av oppdateringsvarselet (`use-app-version`).
 */
export function useVisibilityContext() {
  const { activeUser } = useImpersonation();
  const { data: levels } = useFeatureFlagLevels();
  const role = activeUser?.role;
  const isFlagOn = useMemo(
    () => (key: FeatureFlagKey) => resolveFeatureFlagLevel(levels?.[key], role, key),
    [levels, role],
  );
  return { role, isFlagOn };
}

/**
 * Systemrapporten (én oppføring per PR), filtrert på rolle og feature-flagg.
 * Siden viser den bare for superadmin (VIB-2832); filteret her gjelder fortsatt,
 * så en superadmin som impersonerer ser punktene slik den brukeren ville sett dem.
 */
export function useVisibleChangelog(): ChangelogEntry[] {
  const { role, isFlagOn } = useVisibilityContext();
  return useMemo(() => visibleEntries(CHANGELOG_ENTRIES, role, isFlagOn), [role, isFlagOn]);
}

/** Utrullinger synlige for aktiv bruker (VIB-2832). Det er disse som gir popup. */
export function useVisibleReleases(): ReleaseNote[] {
  const { role, isFlagOn } = useVisibilityContext();
  return useMemo(() => visibleReleases(RELEASE_NOTES, role, isFlagOn), [role, isFlagOn]);
}

/**
 * `users.changelog_seen_id` for én bruker. `data === null` betyr aldri sett,
 * `undefined` betyr ikke lastet ennå. queryKey er registrert under `users`
 * i CACHE_REGISTRY.
 */
export function useChangelogSeen(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["changelog-seen", userId],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from("users")
        .select("changelog_seen_id")
        .eq("id", userId!)
        .maybeSingle();
      if (error) throw error;
      return (data?.changelog_seen_id as string | null | undefined) ?? null;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

interface MarkSeenInput {
  /** Den faktisk innloggede brukeren (RPC-en skriver uansett bare egen rad). */
  userId: string;
  entryId: string;
}

/** Kvitterer for nyeste oppføring via SECURITY DEFINER-RPC (RLS tillater ikke self-update på users). */
export function useMarkChangelogSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ entryId }: MarkSeenInput) => {
      const { error } = await supabase.rpc("mark_changelog_seen", { p_entry_id: entryId });
      if (error) throw error;
    },
    onSettled: async (_data, _error, { userId }) => {
      await invalidateFor(queryClient, "users", { userId });
    },
  });
}
